












































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import type { DocumentItem } from '@/entities/document/model';
import pdf from '@teckel/vue-pdf';
import PagesSelector from './PagesSelector.vue';
import _ from 'lodash';
import ParticipantStamps from '../participantStamps/ParticipantStamps.vue';
import { getStampCacheKey, getOrLoadStamp, deleteStampCacheKey } from '@/services/stampCache';
import eventBus from '@/utils/eventBus';
import api from '@/utils/axios';

@Component({
  components: { pdf, PagesSelector, ParticipantStamps }
})
export default class NewPdfForm extends Vue {
  @Prop() file!: DocumentItem;

  numPages = 0;
  pdfPages: any[] = [];
  private userPageChange = false;
  private scrollHandler: (() => void) | null = null;

  stampsMap: Record<string, { blobUrl: string; ratio: number }> = {};
  
  private previousContacts: string[] = [];

  pageReady: boolean[] = [];
  private pageObservers: MutationObserver[] = [];
  
  documentsVersion: number = 0;

  get loadingTask() {
    if (!this.file?.blobUri) return null;
    return pdf.createLoadingTask(this.file.blobUri);
  }

  get currentPage(): number {
    return this.$store.getters['currentPage'] || 1;
  }

  created() {
    this.previousContacts = this.getContactsFromTransaction();
  }

  recalcAndSetPagesCount() {
    const task = this.loadingTask;
    if (!task) return;

    task.promise.then(async (pdfDoc: any) => {
      this.numPages = pdfDoc.numPages;
      this.pdfPages = await Promise.all(
        _.range(1, pdfDoc.numPages + 1).map((pageNum: number) => pdfDoc.getPage(pageNum))
      );
      this.$store.commit('SET_PDF_PAGES', this.pdfPages);
      
      this.pageReady = new Array(pdfDoc.numPages + 1).fill(false);
      
      this.$nextTick(() => {
        this.setupPageObservers();
        this.loadAllStamps();
      });
    });
  }

  private setupPageObservers() {
    this.teardownPageObservers();
    
    for (let i = 1; i <= this.numPages; i++) {
      const refs = this.$refs[`pdfPage__${i}`];
      const ref = refs ? (Array.isArray(refs) ? refs[0] : refs) : null;
      const el = ref ? (ref instanceof Vue ? (ref as Vue).$el : ref as HTMLElement) : null;
      
      if (!el) continue;
      
      const observer = new MutationObserver(() => {
        const canvas = el.querySelector('canvas');
        if (canvas && canvas.width > 0 && canvas.height > 0) {
          this.$set(this.pageReady, i, true);
          observer.disconnect();
        }
      });
      
      observer.observe(el, {
        childList: true,
        subtree: true,
      });
      
      this.pageObservers.push(observer);
      
      const canvas = el.querySelector('canvas');
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        this.$set(this.pageReady, i, true);
        observer.disconnect();
      }
    }
  }

  private teardownPageObservers() {
    this.pageObservers.forEach(observer => {
      try {
        observer.disconnect();
      } catch {}
    });
    this.pageObservers = [];
  }

  private getContactsFromTransaction(): string[] {
    const transaction = this.$store.state.transaction;
    if (!transaction) return [];

    const contacts: string[] = [];

    if (transaction.author?.contact) contacts.push(transaction.author.contact);
    if (transaction.preAuthor?.contact) contacts.push(transaction.preAuthor.contact);
    if (transaction.actors) {
      transaction.actors.forEach((actor: any) => {
        if (actor.contact) contacts.push(actor.contact);
        if (actor.preActor?.contact) contacts.push(actor.preActor.contact);
      });
    }

    return _.uniq(contacts.filter(c => c));
  }

  async loadAllStamps() {
    const contacts = this.getContactsFromTransaction();
    if (contacts.length) {
      await this.loadStampsForContacts(contacts);
    }
  }

  async loadStampsForContacts(contacts: string[]) {
    const transactionId = this.$route.params.id;
    const userId = this.$route.params.uid;

    contacts.forEach(contact => {
      const key = getStampCacheKey(transactionId, contact, userId);
      deleteStampCacheKey(key);
      if (this.stampsMap[contact]) {
        URL.revokeObjectURL(this.stampsMap[contact].blobUrl);
      }
      Vue.delete(this.stampsMap, contact);
    });

    await Promise.all(contacts.map(async contact => {
      const key = getStampCacheKey(transactionId, contact, userId);
      const stamp = await getOrLoadStamp(key, () => this.loadAddedUserStamp(contact));
      Vue.set(this.stampsMap, contact, stamp);
    }));
  }

  async loadAddedUserStamp(contact: string) {
    const response = await api.get('/SignatureStamps/AddedStamp', {
      params: {
        transactionId: this.$route.params.id,
        actorContact: contact,
        userId: this.$route.params.uid,
        _t: Date.now()
      },
      responseType: 'blob'
    });
    const blob = response.data;
    const ratio = await this.getImageAspectRatioFromBlob(blob);
    return { blob, ratio };
  }

  private getImageAspectRatioFromBlob(blob: Blob): Promise<number> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img.width / img.height);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(4);
      };
      img.src = url;
    });
  }

  @Watch('file', { immediate: true })
  onFileChange() {
    if (!this.file?.blobUri) return;
    this.recalcAndSetPagesCount();
  }

  @Watch('$store.state.transaction', { deep: true })
  onTransactionChange() {
    const currentContacts = this.getContactsFromTransaction();
    
    if (!_.isEqual(this.previousContacts.sort(), currentContacts.sort())) {
      this.previousContacts = [...currentContacts];
      this.loadAllStamps();
    }
  }

  mounted() {
    eventBus.$on('reload-stamps', this.loadStampsForContacts);
    eventBus.$on('force-reload-pdf', this.onFileChange);
    this.bindScrollListener();
    
    this.$store.watch(
      (state) => state.transaction.documents,
      () => {
        this.documentsVersion++;
      },
      { deep: true }
    );
  }

  beforeDestroy() {
    eventBus.$off('reload-stamps', this.loadStampsForContacts);
    eventBus.$off('force-reload-pdf', this.onFileChange);
    this.unbindScrollListener();
    this.teardownPageObservers();
  }

  private bindScrollListener() {
    const root = this.$refs.pdfContainer as HTMLElement | null;
    if (!root) return;

    this.scrollHandler = () => {
      this.userPageChange = false;

      const root = this.$refs.pdfContainer as HTMLElement | null;
      if (!root) return;

      const containerRect = root.getBoundingClientRect();
      const containerScrollTop = root.scrollTop;
      const containerHeight = root.clientHeight;

      let mostVisiblePage = 1;
      let maxVisibleArea = 0;

      for (let i = 1; i <= this.numPages; i++) {
        const el = this.$refs[`pdfPage__${i}`];
        let target: HTMLElement | null = null;

        if (Array.isArray(el)) {
          for (const item of el) {
            if (item instanceof HTMLElement) {
              target = item;
              break;
            }
          }
        } else if (el instanceof HTMLElement) {
          target = el;
        }

        if (!target) continue;

        const targetRect = target.getBoundingClientRect();
        const pageTop = targetRect.top - containerRect.top + containerScrollTop;
        const pageBottom = pageTop + target.offsetHeight;

        const visibleTop = Math.max(pageTop, containerScrollTop);
        const visibleBottom = Math.min(pageBottom, containerScrollTop + containerHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibleArea) {
          maxVisibleArea = visibleHeight;
          mostVisiblePage = i;
        }
      }

      if (maxVisibleArea > 10 && !this.userPageChange) {
        this.$store.dispatch('setCurrentPage', mostVisiblePage);
      }
    };

    root.addEventListener('scroll', this.scrollHandler);
  }

  private unbindScrollListener() {
    const root = this.$refs.pdfContainer as HTMLElement | null;
    if (root && this.scrollHandler) {
      root.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  getPageRef(page: number) {
    return this.$refs['pdfPage__' + page];
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.numPages) return
    this.$store.dispatch('setCurrentPage', page)
    this.userPageChange = true

    this.$nextTick(() => {
      const refs = this.getPageRef(page)
      const ref = refs ? (Array.isArray(refs) ? refs[0] : refs) : null
      if (!ref) return
      const el = ref instanceof Vue ? (ref as Vue).$el : ref as HTMLElement

      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }
}
